/**
 * Breakdown display manager for product bundles
 * Handles updating and protecting the price breakdown display
 */
(function() {
    'use strict';
    
    window.BundleBreakdownManager = window.BundleBreakdownManager || {};

    /**
     * Update breakdown display with prices and discount
     * @param {HTMLElement} bundleElement - The bundle element
     * @param {number} bikePriceCents - Bike price in cents
     * @param {number} addonsTotalCents - Addons total in cents
     * @param {number} discountAmountCents - Discount amount in cents (applies to bike only)
     * @param {Function} debugLog - Debug logging function
     * @param {Function} debugWarn - Debug warning function
     */
    window.BundleBreakdownManager.updateBreakdownDisplay = function(bundleElement, bikePriceCents, addonsTotalCents, discountAmountCents, debugLog, debugWarn) {
    const breakdownContainer = bundleElement.querySelector('.bundle-price-breakdown');
    const bikePriceElement = bundleElement.querySelector('[data-bundle-bike-price]');
    const bikeDiscountElement = bundleElement.querySelector('[data-bundle-bike-discount]');
    const addonsTotalElement = bundleElement.querySelector('[data-bundle-addons-total]');
    const finalTotalElement = bundleElement.querySelector('[data-bundle-final-total]');
    const discountRow = bundleElement.querySelector('.bundle-discount-row');
    const addonsRow = bundleElement.querySelector('.bundle-addons-row');
    
    if (!breakdownContainer || !bikePriceElement || !finalTotalElement) {
        debugWarn('[ProductBundle] Breakdown elements not found');
        return;
    }
    
    // Parse price text to cents helper
    const parsePriceToCents = (priceText) => {
        if (!priceText) return 0;
        const cleaned = priceText.replace(/[^0-9.,]/g, '').replace(/,/g, '');
        const dollars = parseFloat(cleaned) || 0;
        return Math.round(dollars * 100);
    };
    
    // CRITICAL: If bike price is 0, preserve last known value to prevent clearing
    if (bikePriceCents === 0 && bikePriceElement.textContent.trim()) {
        const lastBikePriceCents = parsePriceToCents(bikePriceElement.textContent.trim());
        if (lastBikePriceCents > 0) {
            debugLog('[ProductBundle] Preserving bike price in updateBreakdownDisplay:', lastBikePriceCents);
            bikePriceCents = lastBikePriceCents;
        }
    }
    
    // CRITICAL: Never update with 0 if we have existing content - preserve it
    if (bikePriceCents === 0 && bikePriceElement.textContent.trim()) {
        debugWarn('[ProductBundle] Skipping breakdown update - bike price is 0 but we have existing content');
        // Just ensure container is visible, don't clear content
        breakdownContainer.style.display = 'block';
        breakdownContainer.style.visibility = 'visible';
        return;
    }
    
    // CRITICAL: Store current values before updating (for recovery if something clears them)
    const currentBikePrice = bikePriceElement.textContent.trim();
    const currentTotal = finalTotalElement.textContent.trim();
    
    // Update bike price
    bikePriceElement.innerHTML = Shopify.formatMoney(bikePriceCents, window.money_format);
    
    // Calculate final total: discounted bike price + addons
    const discountedBikePrice = bikePriceCents - discountAmountCents;
    const finalTotalCents = discountedBikePrice + addonsTotalCents;
    
    // Show/hide discount row
    const hasActiveDiscount = discountAmountCents > 0;
    if (hasActiveDiscount && discountRow && bikeDiscountElement) {
        bikeDiscountElement.innerHTML = Shopify.formatMoney(-discountAmountCents, window.money_format);
        discountRow.style.display = 'flex';
    } else if (discountRow) {
        discountRow.style.display = 'none';
    }
    
    // Show/hide addons row
    // CRITICAL: Only show addons row if addonsTotalCents > 0
    // This ensures unchecked addons don't show $0.00 or stale values
    if (addonsTotalCents > 0 && addonsRow && addonsTotalElement) {
        addonsTotalElement.innerHTML = Shopify.formatMoney(addonsTotalCents, window.money_format);
        addonsRow.style.display = 'flex';
    } else if (addonsRow) {
        // Hide row and clear content when no addons
        addonsRow.style.display = 'none';
        if (addonsTotalElement) {
            addonsTotalElement.innerHTML = '';
        }
    }
    
    // Update final total
    finalTotalElement.innerHTML = Shopify.formatMoney(finalTotalCents, window.money_format);
    
    // CRITICAL: Verify content was actually set (defensive check)
    if (!bikePriceElement.textContent.trim() || bikePriceElement.textContent.trim() === '$0.00') {
        debugWarn('[ProductBundle] ⚠️ Bike price element was cleared! Restoring from stored value...');
        if (currentBikePrice) {
            bikePriceElement.innerHTML = currentBikePrice;
        }
    }
    
    if (!finalTotalElement.textContent.trim() || finalTotalElement.textContent.trim() === '$0.00') {
        debugWarn('[ProductBundle] ⚠️ Final total element was cleared! Restoring from stored value...');
        if (currentTotal) {
            finalTotalElement.innerHTML = currentTotal;
        }
    }
    
    // Show breakdown container - ALWAYS visible
    breakdownContainer.style.display = 'block';
    breakdownContainer.style.visibility = 'visible';
    
    // CRITICAL: Use !important to override any CSS that might hide it
    breakdownContainer.setAttribute('style', 'display: block !important; visibility: visible !important;');
    
    // Store discount amount for persistence
    if (discountAmountCents > 0) {
        breakdownContainer.dataset.discountAmount = discountAmountCents;
    }
    
    // Store current values for recovery
        breakdownContainer.dataset.lastBikePrice = bikePriceCents;
        breakdownContainer.dataset.lastTotal = finalTotalCents;
    };

    /**
     * Setup MutationObserver to protect breakdown from being hidden
     * @param {HTMLElement} bundleElement - The bundle element
     * @param {Function} debugWarn - Debug warning function
     * @returns {Object} Observer and interval references for cleanup
     */
    window.BundleBreakdownManager.setupBreakdownProtection = function(bundleElement, debugWarn) {
    const breakdownContainer = bundleElement.querySelector('.bundle-price-breakdown');
    if (!breakdownContainer) return null;
    
    // MutationObserver to watch for style/class changes
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
                const display = window.getComputedStyle(breakdownContainer).display;
                const visibility = window.getComputedStyle(breakdownContainer).visibility;
                
                if (display === 'none' || visibility === 'hidden') {
                    debugWarn('[ProductBundle] ⚠️ Breakdown was hidden! Restoring visibility...');
                    breakdownContainer.style.display = 'block';
                    breakdownContainer.style.visibility = 'visible';
                    breakdownContainer.setAttribute('style', 'display: block !important; visibility: visible !important;');
                }
            }
        });
    });
    
    const config = {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false,
        subtree: false
    };
    
    observer.observe(breakdownContainer, config);
    
    // Also periodically check (defensive measure)
    const interval = setInterval(() => {
        const display = window.getComputedStyle(breakdownContainer).display;
        const visibility = window.getComputedStyle(breakdownContainer).visibility;
        
        if (display === 'none' || visibility === 'hidden') {
            debugWarn('[ProductBundle] ⚠️ Breakdown hidden in periodic check! Restoring...');
            breakdownContainer.style.display = 'block';
            breakdownContainer.style.visibility = 'visible';
            breakdownContainer.setAttribute('style', 'display: block !important; visibility: visible !important;');
        }
        
        // Also check if content was cleared
        const bikePriceElement = bundleElement.querySelector('[data-bundle-bike-price]');
        const finalTotalElement = bundleElement.querySelector('[data-bundle-final-total]');
        
        if (bikePriceElement && (!bikePriceElement.textContent.trim() || bikePriceElement.textContent.trim() === '$0.00')) {
            const lastBikePrice = breakdownContainer.dataset.lastBikePrice;
            if (lastBikePrice && parseFloat(lastBikePrice) > 0) {
                debugWarn('[ProductBundle] ⚠️ Bike price was cleared! Restoring from dataset...');
                bikePriceElement.innerHTML = Shopify.formatMoney(parseFloat(lastBikePrice), window.money_format);
            }
        }
        
        if (finalTotalElement && (!finalTotalElement.textContent.trim() || finalTotalElement.textContent.trim() === '$0.00')) {
            const lastTotal = breakdownContainer.dataset.lastTotal;
            if (lastTotal && parseFloat(lastTotal) > 0) {
                debugWarn('[ProductBundle] ⚠️ Final total was cleared! Restoring from dataset...');
                finalTotalElement.innerHTML = Shopify.formatMoney(parseFloat(lastTotal), window.money_format);
            }
        }
        }, 500); // Check every 500ms
        
        return { observer, interval };
    };

    /**
     * Ensure breakdown is visible (called before/after variant changes)
     * @param {HTMLElement} bundleElement - The bundle element
     */
    window.BundleBreakdownManager.ensureBreakdownVisible = function(bundleElement) {
        const breakdownContainer = bundleElement.querySelector('.bundle-price-breakdown');
        if (breakdownContainer) {
            breakdownContainer.style.display = 'block';
            breakdownContainer.style.visibility = 'visible';
            breakdownContainer.setAttribute('style', 'display: block !important; visibility: visible !important;');
        }
    };
})();
